import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { CheckCircle2, Gauge, AlertTriangle, Sparkles, Globe, Mail, Palette, Building2, MessageSquare, Bot, Loader2 } from "lucide-react";
import { formatEUR } from "@/lib/constants";
import EmailTestCard from "@/components/EmailTestCard";
import EmailDnsHintCard from "@/components/EmailDnsHintCard";

const TONES = ["freundlich", "professionell", "knapp", "beratend", "verkaufsstark"];
const CHANNELS = ["E-Mail", "SMS", "WhatsApp"];

export default function Settings() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [plan, setPlan] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [upgradeTarget, setUpgradeTarget] = useState("");
  const [upgradeNote, setUpgradeNote] = useState("");
  const [upgradeSending, setUpgradeSending] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState("");
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [dnsChecking, setDnsChecking] = useState(false);
  const [dnsResult, setDnsResult] = useState(null); // {status, detail}

  const loadPlan = useCallback(async () => {
    try { const r = await api.get("/billing/plan"); setPlan(r.data); }
    catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    (async () => {
      const r = await api.get("/settings");
      setC(r.data);
      await loadPlan();
    })();
  }, [loadPlan]);

  // Sprint 8b — Polling solange Offer-Domain SSL pending ist
  useEffect(() => {
    const isPending = c?.offer_ssl_status === "pending" || c?.offer_ssl_activation_in_progress;
    if (!isPending) return;
    let cancelled = false;
    const tick = async () => {
      if (cancelled) return;
      try { const r = await api.get("/settings"); if (!cancelled) setC(r.data); } catch { /* still */ }
    };
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [c?.offer_ssl_status, c?.offer_ssl_activation_in_progress]);


  // Polling: nach Rückkehr von Stripe Checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session_id");
    const cancelled = params.get("stripe_cancelled");
    if (cancelled) {
      toast.info("Zahlung abgebrochen.");
      window.history.replaceState({}, "", "/settings");
      return;
    }
    if (!sid) return;
    let attempts = 0;
    const maxAttempts = 8;
    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/billing/stripe/checkout-status/${sid}`);
        if (r.data.payment_status === "paid") {
          setPaymentStatus({ state: "success", message: `Upgrade auf ${r.data.plan_name} erfolgreich aktiviert.` });
          toast.success(`Upgrade auf ${r.data.plan_name} aktiviert.`);
          await loadPlan();
          window.history.replaceState({}, "", "/settings");
          return;
        }
        if (r.data.status === "expired") {
          setPaymentStatus({ state: "error", message: "Zahlung abgelaufen. Bitte erneut versuchen." });
          window.history.replaceState({}, "", "/settings");
          return;
        }
        if (attempts < maxAttempts) {
          setTimeout(poll, 2000);
        } else {
          setPaymentStatus({ state: "error", message: "Zahlungsstatus konnte nicht bestätigt werden – wir prüfen das im Hintergrund und melden uns." });
          window.history.replaceState({}, "", "/settings");
        }
      } catch (e) {
        setPaymentStatus({ state: "error", message: "Fehler beim Status-Check." });
      }
    };
    // Deferred → vermeidet sync setState-in-effect (R19-Compiler-Hint)
    Promise.resolve().then(() => {
      setPaymentStatus({ state: "checking", message: "Zahlungsstatus wird geprüft …" });
      poll();
    });
  }, [loadPlan]);

  if (!c) return <div className="text-slate-500">Wird geladen …</div>;
  const set = (k, v) => setC({ ...c, [k]: v });

  const save = async () => {
    setSaving(true);
    try {
      const allowed = [
        "name", "contact_name", "email", "phone", "address", "website",
        "default_tone", "max_followups", "default_channel", "auto_ai", "hot_lead_rules",
        "logo_url", "primary_color", "accent_color",
        "support_email", "support_phone", "imprint_url", "privacy_url",
        "custom_offer_domain",
        "email_from_name", "email_from_address", "email_reply_to",
        "followup_enabled",
      ];
      const payload = {};
      for (const k of allowed) if (c[k] !== undefined) payload[k] = c[k];
      const r = await api.patch("/settings", payload);
      setC(r.data);
      toast.success("Einstellungen gespeichert");
    } catch (e) { toast.error(e?.response?.data?.detail?.[0]?.msg || "Fehler beim Speichern"); }
    finally { setSaving(false); }
  };

  const openUpgrade = (target = "") => {
    setUpgradeTarget(target || plan?.recommended_next_plan || "");
    setUpgradeNote("");
    setUpgradeOpen(true);
  };

  const submitUpgrade = async () => {
    setUpgradeSending(true);
    try {
      await api.post("/billing/request-upgrade", { requested_plan: upgradeTarget || null, note: upgradeNote });
      toast.success("Upgrade-Anfrage gesendet. Wir melden uns kurzfristig.");
      setUpgradeOpen(false);
    } catch { toast.error("Fehler"); }
    finally { setUpgradeSending(false); }
  };

  const startCheckout = async (planName, billingCycle = "monthly") => {
    setCheckoutBusy(planName);
    try {
      const r = await api.post("/billing/stripe/create-checkout", {
        plan_name: planName,
        billing_cycle: billingCycle,
        origin_url: window.location.origin,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Stripe konnte nicht initialisiert werden.");
      setCheckoutBusy("");
    }
  };

  const openCustomerPortal = async () => {
    setCheckoutBusy("portal");
    try {
      const r = await api.post("/billing/stripe/customer-portal", {
        return_url: `${window.location.origin}/settings`,
      });
      window.location.href = r.data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kundenportal konnte nicht geöffnet werden.");
      setCheckoutBusy("");
    }
  };

  const checkDns = async () => {
    setDnsChecking(true);
    setDnsResult(null);
    try {
      const r = await api.post("/settings/custom-domain/check");
      setDnsResult({ status: r.data.status, detail: r.data.detail });
      // Settings neu laden, damit Status-Badge / verified_at aktualisiert werden
      const fresh = await api.get("/settings");
      setC(fresh.data);
      if (r.data.status === "active") toast.success("Custom-Domain ist aktiv!");
      else if (r.data.status === "pending_dns") toast.info("DNS noch nicht erreichbar.");
      else if (r.data.status === "error") toast.error("DNS-Fehler.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "DNS-Check fehlgeschlagen.");
    } finally {
      setDnsChecking(false);
    }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-page">
      <header>
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Einstellungen</h1>
        <p className="mt-1 text-slate-600">Firmendaten, Tonalität, KI-Verhalten und Plan</p>
      </header>

      {paymentStatus && (
        <Card data-testid="payment-status-banner" className={
          paymentStatus.state === "success" ? "border-emerald-200 bg-emerald-50 p-4" :
          paymentStatus.state === "error" ? "border-red-200 bg-red-50 p-4" :
          "border-amber-200 bg-amber-50 p-4"
        }>
          <p className="text-sm font-medium">{paymentStatus.message}</p>
        </Card>
      )}

      {plan && (
        <Tabs defaultValue="billing" className="w-full" data-testid="settings-tabs">
          <TabsList className="bg-white border border-slate-200 flex flex-wrap h-auto p-1 gap-1">
            <TabsTrigger data-testid="settings-tab-billing" value="billing" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Gauge className="w-3.5 h-3.5 mr-1.5" /> Abrechnung &amp; Plan
            </TabsTrigger>
            <TabsTrigger data-testid="settings-tab-general" value="general" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Building2 className="w-3.5 h-3.5 mr-1.5" /> Allgemein
            </TabsTrigger>
            <TabsTrigger data-testid="settings-tab-branding" value="branding" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Palette className="w-3.5 h-3.5 mr-1.5" /> Branding
            </TabsTrigger>
            <TabsTrigger data-testid="settings-tab-email" value="email" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Mail className="w-3.5 h-3.5 mr-1.5" /> E-Mail
            </TabsTrigger>
            <TabsTrigger data-testid="settings-tab-domain" value="domain" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Globe className="w-3.5 h-3.5 mr-1.5" /> Domain
            </TabsTrigger>
            <TabsTrigger data-testid="settings-tab-ai" value="ai" className="data-[state=active]:bg-orange-600 data-[state=active]:text-white">
              <Bot className="w-3.5 h-3.5 mr-1.5" /> Kommunikation &amp; KI
            </TabsTrigger>
          </TabsList>

          {/* ===== Tab: Abrechnung & Plan ===== */}
          <TabsContent value="billing" className="mt-4 space-y-6">
            <PlanCard plan={plan} onUpgrade={openUpgrade} onCheckout={startCheckout} onPortal={openCustomerPortal} checkoutBusy={checkoutBusy} />
          </TabsContent>

        {/* ===== Tab: Allgemein ===== */}
        <TabsContent value="general" className="mt-4 space-y-6">
          <Card className="border-slate-200 p-6 space-y-4">
            <h2 className="text-lg font-semibold">Firmendaten</h2>
            <Field label="Firmenname"><Input data-testid="set-name" value={c.name || ""} onChange={(e) => set("name", e.target.value)} /></Field>
            <Field label="Ansprechpartner"><Input data-testid="set-contact" value={c.contact_name || ""} onChange={(e) => set("contact_name", e.target.value)} /></Field>
            <Field label="E-Mail"><Input data-testid="set-email" value={c.email || ""} onChange={(e) => set("email", e.target.value)} /></Field>
            <Field label="Telefon"><Input data-testid="set-phone" value={c.phone || ""} onChange={(e) => set("phone", e.target.value)} /></Field>
            <Field label="Adresse"><Input data-testid="set-address" value={c.address || ""} onChange={(e) => set("address", e.target.value)} placeholder="Straße, PLZ Ort" /></Field>
            <Field label="Website"><Input data-testid="set-website" value={c.website || ""} onChange={(e) => set("website", e.target.value)} placeholder="https://…" /></Field>
          </Card>
        </TabsContent>

        {/* ===== Tab: Branding ===== */}
        <TabsContent value="branding" className="mt-4 space-y-6">
          <Card className="border-slate-200 p-6 space-y-4" data-testid="branding-card">
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-orange-600" />
              <h2 className="text-lg font-semibold">Branding &amp; Auftritt</h2>
            </div>
            <p className="text-sm text-slate-500 -mt-2">Wird auf der öffentlichen Angebotsseite und im Konfigurator verwendet.</p>
            <Field label="Logo-URL">
              <div className="flex items-center gap-3">
                <Input data-testid="set-logo-url" value={c.logo_url || ""} onChange={(e) => set("logo_url", e.target.value)} placeholder="https://example.com/logo.png" className="flex-1" />
                {c.logo_url ? (
                  <img src={c.logo_url} alt="Logo" className="h-10 max-w-[120px] object-contain rounded border border-slate-200 bg-white p-1" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : null}
              </div>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Primärfarbe">
                <div className="flex items-center gap-2">
                  <input type="color" data-testid="set-primary-color-picker" value={c.primary_color || "#f97316"} onChange={(e) => set("primary_color", e.target.value)} className="h-10 w-12 rounded border border-slate-200 cursor-pointer" />
                  <Input data-testid="set-primary-color" value={c.primary_color || ""} onChange={(e) => set("primary_color", e.target.value)} placeholder="#f97316" className="flex-1 font-mono" />
                </div>
              </Field>
              <Field label="Akzentfarbe">
                <div className="flex items-center gap-2">
                  <input type="color" data-testid="set-accent-color-picker" value={c.accent_color || "#0f172a"} onChange={(e) => set("accent_color", e.target.value)} className="h-10 w-12 rounded border border-slate-200 cursor-pointer" />
                  <Input data-testid="set-accent-color" value={c.accent_color || ""} onChange={(e) => set("accent_color", e.target.value)} placeholder="#0f172a" className="flex-1 font-mono" />
                </div>
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Support-E-Mail"><Input data-testid="set-support-email" value={c.support_email || ""} onChange={(e) => set("support_email", e.target.value)} placeholder="support@…" /></Field>
              <Field label="Support-Telefon"><Input data-testid="set-support-phone" value={c.support_phone || ""} onChange={(e) => set("support_phone", e.target.value)} placeholder="+49 …" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Impressum-URL"><Input data-testid="set-imprint-url" value={c.imprint_url || ""} onChange={(e) => set("imprint_url", e.target.value)} placeholder="https://…/impressum" /></Field>
              <Field label="Datenschutz-URL"><Input data-testid="set-privacy-url" value={c.privacy_url || ""} onChange={(e) => set("privacy_url", e.target.value)} placeholder="https://…/datenschutz" /></Field>
            </div>
          </Card>
        </TabsContent>

        {/* ===== Tab: E-Mail ===== */}
        <TabsContent value="email" className="mt-4 space-y-6">
          <EmailTestCard variant="tenant" />
          <EmailDnsHintCard variant="tenant" />

          <Card className="border-slate-200 p-6 space-y-4" data-testid="email-ci-card">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-orange-600" />
              <h2 className="text-lg font-semibold">E-Mail-CI</h2>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Absender-Name (From)"><Input data-testid="set-email-from-name" value={c.email_from_name || ""} onChange={(e) => set("email_from_name", e.target.value)} placeholder="Solar Müller GmbH" /></Field>
              <Field label="Antwort-Adresse (Reply-To)"><Input data-testid="set-email-reply-to" value={c.email_reply_to || ""} onChange={(e) => set("email_reply_to", e.target.value)} placeholder="vertrieb@…" /></Field>
            </div>
            <Field label="Gewünschte Absender-Adresse">
              <div className="flex items-center gap-3">
                <Input data-testid="set-email-from-address" value={c.email_from_address || ""} onChange={(e) => set("email_from_address", e.target.value)} placeholder="angebote@ihrebetrieb.de" className="flex-1" />
                <Badge variant="outline" className="text-xs whitespace-nowrap">{c.email_sender_status === "verified" ? "Domain verifiziert" : "Reply-To-Modus"}</Badge>
              </div>
            </Field>
            <div className="bg-slate-50 border border-slate-200 rounded p-3 text-xs space-y-1">
              <p className="font-semibold text-slate-700">Vorschau Absender</p>
              <p><span className="text-slate-500">Von:</span> {c.email_from_name || c.name || "Ihre Firma"} &lt;noreply@revenioo.ai&gt;</p>
              <p><span className="text-slate-500">Antwort an:</span> {c.email_reply_to || c.email || "—"}</p>
            </div>
            <p className="text-xs text-amber-700 flex items-start gap-2"><AlertTriangle className="w-3 h-3 mt-0.5 flex-shrink-0" /> Vollständige Domain-Verifizierung (SPF/DKIM/DMARC) kommt in einer späteren Phase. Aktuell wird Reply-To zur Korrespondenz verwendet.</p>
          </Card>
        </TabsContent>

        {/* ===== Tab: Domain ===== */}
        <TabsContent value="domain" className="mt-4 space-y-6">
          <Card className="border-slate-200 p-6 space-y-4" data-testid="domain-card">
            <div className="flex items-center gap-2">
              <Globe className="w-4 h-4 text-orange-600" />
              <h2 className="text-lg font-semibold">Angebotsdomain (optional)</h2>
            </div>
            <p className="text-sm text-slate-500 -mt-2">Diese Domain wird für <b>Angebotsseiten und Endkundenkommunikation</b> Ihrer Solarfirma verwendet (z.B. <span className="font-mono">angebot.solar-mueller.de</span>). Die Agentur-Portal-Domain wird separat in der Agentur-Verwaltung konfiguriert.</p>
            <Field label="Custom-Domain">
              <Input data-testid="set-custom-domain" value={c.custom_offer_domain || ""} onChange={(e) => set("custom_offer_domain", e.target.value)} placeholder="angebot.ihrebetrieb.de" className="font-mono" />
            </Field>
            {(c.custom_offer_domain || "").trim() ? (
              <div className="bg-slate-50 rounded-lg p-3 text-sm space-y-2 border border-slate-200">
                <p className="font-semibold text-slate-700">DNS-Anweisung</p>
                <p className="text-slate-600">Setzen Sie bei Ihrem DNS-Provider einen <span className="font-mono font-semibold">CNAME</span>-Eintrag:</p>
                <pre className="bg-white border border-slate-200 rounded px-3 py-2 text-xs overflow-x-auto"><span className="text-slate-500">CNAME</span>  {c.custom_offer_domain}  →  {c.custom_offer_domain_dns_target || "cname.revenioo.ai"}</pre>
                <div className="flex items-center justify-between gap-3 pt-1">
                  <DomainStatusBadge status={c.custom_offer_domain_status} verifiedAt={c.custom_offer_domain_verified_at} />
                  <Button data-testid="check-dns-btn" onClick={checkDns} disabled={dnsChecking} size="sm" className="bg-slate-900 hover:bg-slate-800 text-white">
                    {dnsChecking ? "Prüfe …" : "DNS prüfen"}
                  </Button>
                </div>
                {dnsResult && (
                  <p data-testid="dns-result" className={`text-xs ${dnsResult.status === "active" ? "text-emerald-700" : dnsResult.status === "error" ? "text-red-700" : "text-slate-600"}`}>
                    {dnsResult.detail}
                  </p>
                )}
                {c.custom_offer_domain_status === "active" && (
                  <p className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> DNS aktiv — Angebotslinks werden über diese Domain versendet.</p>
                )}
                {/* Sprint 8b — SSL-Aktivierung */}
                {(c.custom_offer_domain_status === "verified" || c.custom_offer_domain_status === "active") && (() => {
                  const s = c.offer_ssl_status || "not_started";
                  if (s === "active") {
                    return <p data-testid="offer-domain-ssl-status" className="text-xs text-emerald-700 font-medium flex items-center gap-1"><CheckCircle2 className="w-3 h-3" /> HTTPS aktiv</p>;
                  }
                  if (s === "pending" || c.offer_ssl_activation_in_progress) {
                    return <p data-testid="offer-domain-ssl-status" className="text-xs text-amber-700 font-medium flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> SSL wird eingerichtet (ca. 30–90 s) …</p>;
                  }
                  return (
                    <div className="space-y-2">
                      <div className="flex items-center justify-between gap-2">
                        <p data-testid="offer-domain-ssl-status" className={`text-xs flex items-center gap-1 font-medium ${s === "error" ? "text-red-700" : "text-slate-600"}`}>
                          {s === "error" ? "SSL-Fehler" : "DNS verifiziert — SSL noch nicht aktiviert"}
                        </p>
                        <Button
                          data-testid="offer-activate-ssl-btn"
                          onClick={async () => {
                            try {
                              const r = await api.post("/settings/offer-domain/activate-ssl");
                              if (r.data?.ok) toast.success("HTTPS aktiviert!");
                              else toast.error(r.data?.error || "SSL-Aktivierung fehlgeschlagen");
                              const fresh = await api.get("/settings");
                              setC(fresh.data);
                            } catch (e) {
                              toast.error(e?.response?.data?.detail || "SSL-Aktivierung fehlgeschlagen");
                            }
                          }}
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700 text-white h-7 text-xs"
                        >
                          {s === "error" ? "Erneut versuchen" : "SSL aktivieren"}
                        </Button>
                      </div>
                      {s === "error" && c.offer_ssl_error_message && (
                        <p data-testid="offer-ssl-error" className="text-[11px] text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1 break-words">
                          {c.offer_ssl_error_message}
                        </p>
                      )}
                    </div>
                  );
                })()}
              </div>
            ) : (
              <p className="text-xs text-slate-400">Keine Domain gesetzt – Angebotslinks gehen über die Standard-Adresse.</p>
            )}
          </Card>
        </TabsContent>

        {/* ===== Tab: Kommunikation & KI ===== */}
        <TabsContent value="ai" className="mt-4 space-y-6">
          <Card className="border-slate-200 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <MessageSquare className="w-4 h-4 text-orange-600" />
              <h2 className="text-lg font-semibold">Kommunikation</h2>
            </div>
            <Field label="Standard-Tonalität">
              <Select value={c.default_tone} onValueChange={(v) => set("default_tone", v)}>
                <SelectTrigger data-testid="set-tone"><SelectValue /></SelectTrigger>
                <SelectContent>{TONES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Standard-Kanal">
              <Select value={c.default_channel} onValueChange={(v) => set("default_channel", v)}>
                <SelectTrigger data-testid="set-channel"><SelectValue /></SelectTrigger>
                <SelectContent>{CHANNELS.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Max. Anzahl Nachfassnachrichten"><Input data-testid="set-maxfup" type="number" value={c.max_followups} onChange={(e) => set("max_followups", parseInt(e.target.value) || 0)} /></Field>
          </Card>

          <Card className="border-slate-200 p-6 space-y-4">
            <div className="flex items-center gap-2">
              <Bot className="w-4 h-4 text-orange-600" />
              <h2 className="text-lg font-semibold">KI-Verhalten</h2>
            </div>
            <ToggleRow label="Automatische KI-Antworten aktivieren" testid="set-auto-ai" checked={c.auto_ai} onChange={(v) => set("auto_ai", v)} />
            <ToggleRow label="Hot-Lead-Regeln aktivieren" testid="set-hot" checked={c.hot_lead_rules} onChange={(v) => set("hot_lead_rules", v)} />
          </Card>

          <Card className="border-slate-200 p-6 space-y-4" data-testid="followup-settings-card">
            <h2 className="text-lg font-semibold">Automatische Follow-ups</h2>
            <p className="text-sm text-slate-500 -mt-2">
              Wenn aktiviert, versendet revenioo.ai geplante Follow-up-E-Mails automatisch (24h / 3 Tage / 7 Tage nach Angebotsversand),
              solange ein Lead nicht pausiert, gewonnen, verloren oder abgemeldet ist.
            </p>
            <ToggleRow
              label="Automatische Follow-ups aktivieren"
              testid="set-followup-enabled"
              checked={c.followup_enabled !== false}
              onChange={(v) => set("followup_enabled", v)}
            />
          </Card>
        </TabsContent>
      </Tabs>
      )}

      <div className="sticky bottom-4 z-10 flex justify-end">
        <Button data-testid="save-settings-btn" onClick={save} disabled={saving} className="bg-orange-600 hover:bg-orange-700 text-white shadow-lg">
          {saving ? "Speichern …" : "Speichern"}
        </Button>
      </div>

      <Dialog open={upgradeOpen} onOpenChange={(o) => { if (!upgradeSending) setUpgradeOpen(o); }}>
        <DialogContent data-testid="upgrade-dialog" className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Upgrade anfragen</DialogTitle>
            <DialogDescription>
              Sag uns kurz, welchen Plan du möchtest – wir melden uns persönlich. (Demo-Modus, keine echte Zahlung.)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-sm">Gewünschter Plan</Label>
              <Select value={upgradeTarget} onValueChange={setUpgradeTarget}>
                <SelectTrigger data-testid="upgrade-target-select" className="mt-1.5"><SelectValue placeholder="Plan wählen" /></SelectTrigger>
                <SelectContent>
                  {(plan?.all_plans || []).map((p) => (
                    <SelectItem key={p.plan_name} value={p.plan_name}>{p.plan_name} – {p.plan_price} €/Monat · bis {p.monthly_lead_limit} Leads</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-sm">Notiz (optional)</Label>
              <Textarea data-testid="upgrade-note" value={upgradeNote} onChange={(e) => setUpgradeNote(e.target.value)} rows={3} placeholder="z. B. Wir verarbeiten aktuell ~400 Leads/Monat" className="mt-1.5" />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button data-testid="upgrade-cancel" variant="outline" onClick={() => setUpgradeOpen(false)} disabled={upgradeSending}>Abbrechen</Button>
            <Button data-testid="upgrade-submit" onClick={submitUpgrade} disabled={upgradeSending} className="bg-orange-600 hover:bg-orange-700 text-white">
              {upgradeSending ? "Sende …" : "Anfrage senden"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const PlanCard = ({ plan, onUpgrade, onCheckout, onPortal, checkoutBusy }) => {
  const [cycle, setCycle] = useState("monthly");
  const pct = plan.monthly_lead_limit > 0 ? Math.min(100, Math.round((plan.processed_leads_current_month / plan.monthly_lead_limit) * 100)) : 0;
  const barColor = plan.limit_reached ? "bg-red-500" : plan.near_limit ? "bg-amber-500" : "bg-emerald-500";
  const hasSubscription = !!plan.stripe_subscription_id;
  const subStatus = plan.stripe_subscription_status;
  const cancelAtEnd = plan.subscription_cancel_at_period_end;
  const isYearly = cycle === "yearly";
  const firstPlan = (plan.all_plans || [])[0];
  const savingsPct = firstPlan?.yearly_price ? Math.round((1 - firstPlan.yearly_price / (firstPlan.plan_price * 12)) * 100) : 0;
  const currentPlanData = (plan.all_plans || []).find((p) => p.plan_name === plan.plan_name);
  return (
    <Card className="border-slate-200 p-6" data-testid="settings-plan-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-lg bg-slate-50 text-slate-700"><Gauge className="w-5 h-5" /></div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.15em] text-slate-500">Abrechnung & Plan</p>
            <p className="text-lg font-bold text-slate-900 mt-0.5" style={{ fontFamily: "Manrope, sans-serif" }}>
              Aktueller Plan: <span data-testid="settings-plan-name">{plan.plan_name}</span> · {formatEUR(plan.plan_price)} / Monat
            </p>
            {plan.billing_cycle === "yearly" && (
              <p className="text-xs text-slate-500 mt-0.5" data-testid="settings-billing-cycle-note">
                Jährliche Abrechnung{currentPlanData?.yearly_price ? ` · ${formatEUR(currentPlanData.yearly_price)} pro Jahr` : ""}
              </p>
            )}
            {hasSubscription && (
              <div className="mt-1.5 flex items-center gap-2 flex-wrap text-xs" data-testid="subscription-status">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold ${
                  subStatus === "active" || subStatus === "trialing" ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : subStatus === "past_due" || subStatus === "unpaid" ? "bg-amber-50 text-amber-800 border border-amber-200"
                  : "bg-slate-100 text-slate-600 border border-slate-200"
                }`}>
                  Abo: {subStatus || "—"}
                </span>
                {cancelAtEnd && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-700 border border-red-200 font-semibold">
                    Endet zum Periodenende
                  </span>
                )}
                {plan.subscription_current_period_end && (
                  <span className="text-slate-500">Nächste Abrechnung: {new Date(plan.subscription_current_period_end).toLocaleDateString("de-DE")}</span>
                )}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {hasSubscription && onPortal && (
            <Button data-testid="customer-portal-btn" onClick={onPortal} disabled={checkoutBusy === "portal"} variant="outline" className="border-slate-300">
              {checkoutBusy === "portal" ? "Öffne …" : "Abo verwalten"}
            </Button>
          )}
          <Button data-testid="settings-upgrade-btn" onClick={() => onUpgrade()} variant="outline" className="border-orange-300 text-orange-700 hover:bg-orange-50">
            <Sparkles className="w-4 h-4 mr-1.5" /> Upgrade anfragen
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mt-5 text-sm">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Verarbeitete Leads</p>
          <p className="text-slate-900 font-bold text-base mt-0.5" data-testid="settings-processed-leads">{plan.processed_leads_current_month}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Monatliches Limit</p>
          <p className="text-slate-900 font-bold text-base mt-0.5" data-testid="settings-monthly-limit">{plan.monthly_lead_limit}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Verbleibend</p>
          <p className="text-slate-900 font-bold text-base mt-0.5" data-testid="settings-remaining-leads">{plan.remaining_leads}</p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Abrechnungsmonat</p>
          <p className="text-slate-900 font-bold text-base mt-0.5">{plan.billing_month}</p>
        </div>
      </div>

      <div className="mt-4 h-2 w-full rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
      </div>

      {plan.limit_reached && (
        <div data-testid="settings-limit-reached" className="mt-3 flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Leadlimit erreicht. {plan.recommended_next_plan ? `Empfehlung: Upgrade auf ${plan.recommended_next_plan}.` : "Bitte Upgrade anfragen, um weitere Leads zu verarbeiten."}</span>
        </div>
      )}
      {plan.near_limit && (
        <div data-testid="settings-near-limit" className="mt-3 flex items-start gap-2 text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-md p-3">
          <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <span>Sie nähern sich Ihrem monatlichen Leadlimit.</span>
        </div>
      )}

      <div className="mt-5 pt-5 border-t border-slate-100">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <p className="text-xs uppercase tracking-wider text-slate-500 font-semibold">Alle Pakete – einheitlicher Funktionsumfang</p>
          <div className="inline-flex items-center gap-1 bg-slate-100 rounded-full p-0.5">
            <button
              type="button"
              data-testid="settings-cycle-monthly"
              onClick={() => setCycle("monthly")}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${!isYearly ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Monatlich
            </button>
            <button
              type="button"
              data-testid="settings-cycle-yearly"
              onClick={() => setCycle("yearly")}
              className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors flex items-center gap-1 ${isYearly ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            >
              Jährlich
              {savingsPct > 0 && (
                <span className="inline-flex items-center px-1 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[9px] font-bold">{savingsPct} % gespart</span>
              )}
            </button>
          </div>
        </div>
        <p className="text-xs text-slate-600 mb-3">Alle Pakete enthalten alle Funktionen. Sie zahlen nur nach monatlichem Leadvolumen. Keine Nutzerlimits, keine Feature-Sperren, keine separaten Angebotslimits.</p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {(plan.all_plans || []).map((p) => {
            const isCurrent = p.plan_name === plan.plan_name;
            const shownMonthly = isYearly && p.yearly_price ? Math.round(p.yearly_price / 12) : p.plan_price;
            return (
              <div key={p.plan_name} data-testid={`settings-plan-tier-${p.plan_name.toLowerCase()}`} className={`rounded-lg border p-3 text-sm flex flex-col ${isCurrent ? "border-orange-300 bg-orange-50/50" : "border-slate-200 bg-white"}`}>
                <div className="flex items-center justify-between">
                  <p className="font-bold text-slate-900">{p.plan_name}</p>
                  {isCurrent && <span className="text-[10px] uppercase tracking-wider font-bold text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">Aktuell</span>}
                </div>
                <p className="text-slate-700 mt-0.5"><span className="font-bold text-base">{formatEUR(shownMonthly)}</span> / Monat</p>
                {isYearly && p.yearly_price && (
                  <p className="text-[11px] text-slate-500" data-testid={`settings-tier-yearly-note-${p.plan_name.toLowerCase()}`}>{formatEUR(p.yearly_price)} jährlich abgerechnet</p>
                )}
                <p className="text-xs text-slate-500 mt-0.5">Bis {p.monthly_lead_limit} Leads/Monat</p>
                <ul className="mt-2 space-y-1 flex-1">
                  {p.features.slice(0, 2).map((f) => (
                    <li key={f} className="text-xs text-slate-600 flex gap-1.5"><CheckCircle2 className="w-3 h-3 text-emerald-600 flex-shrink-0 mt-0.5" />{f}</li>
                  ))}
                </ul>
                {!isCurrent && onCheckout && (
                  <Button
                    data-testid={`checkout-${p.plan_name.toLowerCase()}-btn`}
                    size="sm"
                    onClick={() => onCheckout(p.plan_name, cycle)}
                    disabled={checkoutBusy === p.plan_name}
                    className="mt-3 bg-orange-600 hover:bg-orange-700 text-white"
                  >
                    {checkoutBusy === p.plan_name ? "Weiterleitung …" : `Zu ${p.plan_name} wechseln`}
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
};

const Field = ({ label, children }) => (
  <div>
    <Label className="text-sm font-medium text-slate-700 mb-1.5 block">{label}</Label>
    {children}
  </div>
);

const ToggleRow = ({ label, checked, onChange, testid }) => (
  <div className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-700">{label}</span>
    <Switch data-testid={testid} checked={checked} onCheckedChange={onChange} />
  </div>
);

const DOMAIN_STATUS_LABEL = {
  not_configured: { label: "Nicht konfiguriert", className: "bg-slate-200 text-slate-700" },
  pending_dns: { label: "DNS ausstehend", className: "bg-amber-100 text-amber-800 border-amber-200" },
  verified: { label: "Verifiziert", className: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  active: { label: "Aktiv", className: "bg-emerald-600 text-white" },
  error: { label: "Fehler", className: "bg-red-100 text-red-800 border-red-200" },
};

const DomainStatusBadge = ({ status, verifiedAt }) => {
  const meta = DOMAIN_STATUS_LABEL[status] || DOMAIN_STATUS_LABEL.not_configured;
  return (
    <div className="flex items-center gap-2">
      <span data-testid="domain-status-badge" className={`text-xs font-semibold px-2 py-1 rounded border ${meta.className}`}>
        {meta.label}
      </span>
      {verifiedAt && (
        <span className="text-[10px] text-slate-500">seit {new Date(verifiedAt).toLocaleDateString("de-DE")}</span>
      )}
    </div>
  );
};
